import type React from "react";
import {useState} from "react";
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import {Close} from "@mui/icons-material";
import {useRequestChainCreditsMutation} from "~/pages/supplier-360/tabs/SupplierValueChain/hooks/useChainCreditMutations";
import type {ChainCreditEntitlement} from "~/pages/supplier-360/tabs/SupplierValueChain/valueChainCreditTypes";
import {
    CREDIT_PACK_STEP,
    CREDIT_PACKS,
    creditsUsed,
    customCreditsError,
} from "~/pages/supplier-360/tabs/SupplierValueChain/chainCredits";
import {GreyPaper} from "~/components/GreyPaper/GreyPaper";
import {PackTile} from "~/components/PackTile/PackTile";

type PackChoice = typeof CREDIT_PACKS[number] | 'custom';

type ChainRequestCreditsDialogProps = {
    entitlement: ChainCreditEntitlement;
    onClose: () => void;
};

/**
 * "Request value chain credits" (`ChainRequestModal` in the design). Credits come in packs of 100:
 * a custom amount must be a multiple of 100. The design's approver select is free text here,
 * since no endpoint lists approvers.
 */
export const ChainRequestCreditsDialog: React.FC<ChainRequestCreditsDialogProps> = ({entitlement, onClose}) => {
    const request = useRequestChainCreditsMutation();
    const [pack, setPack] = useState<PackChoice>(CREDIT_PACKS[0]);
    const [custom, setCustom] = useState('');
    const [approver, setApprover] = useState('');
    const [submitted, setSubmitted] = useState(false);

    const isCustom = pack === 'custom';
    const raw = isCustom ? Number.parseInt(custom, 10) || 0 : pack;
    const amountValid = raw >= CREDIT_PACK_STEP && raw % CREDIT_PACK_STEP === 0;
    const amount = amountValid ? raw : 0;
    const showError = isCustom && !amountValid && (custom !== '' || submitted);
    const remaining = entitlement.credit_balance;
    const approverName = approver.trim().split(' · ')[0];
    const closeIfIdle = () => {
        if (!request.isPending) onClose();
    };

    const submit = () => {
        setSubmitted(true);
        if (!amountValid || request.isPending) return;
        request.mutate({entitlement, requestedAmount: amount, approver: approver.trim()});
    };

    if (request.isSuccess) {
        const sent = request.data;
        const sentTo = sent.approver ? ` from ${sent.approver.split(' · ')[0]}` : '';
        return (
            <Dialog open onClose={onClose} fullWidth slotProps={{paper: {sx: {maxWidth: 530}}}} aria-labelledby="chain-request-sent-title">
                <DialogTitle id="chain-request-sent-title" sx={{pr: 6, fontSize: 16}}>
                    Request sent
                    <IconButton onClick={onClose} title="Close" size="small" sx={{position: 'absolute', top: 12, right: 12}}>
                        <Close fontSize="small"/>
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary">
                        <b>{`${sent.requested_amount} company credits`}</b>
                        {` requested${sentTo}. You'll see the new balance here as soon as it's approved, nothing is charged before that.`}
                    </Typography>
                    <GreyPaper sx={{mt: 2}}>
                        <Typography variant="caption" color="text.secondary">
                            Pending requests appear on the Value chain tab and in Manage credits, so the rest of the
                            team knows more capacity is coming.
                        </Typography>
                    </GreyPaper>
                </DialogContent>
                <DialogActions sx={{px: 2.5, pb: 2.5}}>
                    <Button variant="contained" onClick={onClose} sx={{textTransform: 'none'}}>Done</Button>
                </DialogActions>
            </Dialog>
        );
    }

    return (
        <Dialog
            open
            onClose={closeIfIdle}
            fullWidth
            aria-labelledby="chain-request-title"
            slotProps={{paper: {sx: {maxWidth: 610}}}}
        >
            <DialogTitle id="chain-request-title" sx={{pr: 6, fontSize: 16}}>
                Request value chain credits
                <IconButton onClick={closeIfIdle} title="Close" size="small" sx={{position: 'absolute', top: 12, right: 12}}>
                    <Close fontSize="small"/>
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Stack gap={2}>
                    <Typography variant="body2" color="text.secondary">
                        You have mapped <b>{`${creditsUsed(entitlement)} of ${entitlement.credits_granted}`}</b> companies.
                        Ask for more capacity: one credit maps one company's full chain.
                    </Typography>

                    <Stack gap={0.75}>
                        <Typography variant="caption" fontWeight={600} color="text.secondary">How many companies</Typography>
                        <Stack direction="row" gap={1}>
                            {CREDIT_PACKS.map((size) => (
                                <PackTile
                                    key={size}
                                    selected={pack === size}
                                    title={String(size)}
                                    subtitle="companies"
                                    onClick={() => setPack(size)}
                                />
                            ))}
                            <PackTile
                                selected={isCustom}
                                title="Custom"
                                subtitle="set below"
                                sx={{'& :first-child': {fontWeight: '400', fontSize: '13px'}}}
                                onClick={() => setPack('custom')}
                            />
                        </Stack>
                    </Stack>

                    {isCustom && (
                        <TextField
                            label="Number of companies"
                            size="small"
                            autoFocus
                            value={custom}
                            placeholder="e.g. 400"
                            error={showError}
                            helperText={showError
                                ? customCreditsError(raw)
                                : `Multiples of ${CREDIT_PACK_STEP} only, for example 400 or 500.`}
                            slotProps={{htmlInput: {inputMode: 'numeric', 'aria-invalid': showError}}}
                            onChange={event => setCustom(event.target.value.replace(/[^0-9]/g, '').slice(0, 5))}
                        />
                    )}

                    <TextField
                        label="Approver (optional)"
                        size="small"
                        value={approver}
                        placeholder="e.g. Elena Rossi · Procurement Ops"
                        slotProps={{htmlInput: {maxLength: 128}}}
                        onChange={event => setApprover(event.target.value)}
                    />

                    <GreyPaper>
                        <Typography variant="caption" color="text.secondary">
                            Requesting <b>{`${amount || '—'} credits`}</b> takes your entitlement to{' '}
                            <b>{`${entitlement.credits_granted + amount} companies`}</b>
                            {` (${remaining + amount} unused). No charge until ${approverName || 'it is approved'}${approverName ? ' approves' : ''}.`}
                        </Typography>
                    </GreyPaper>
                </Stack>
            </DialogContent>
            <DialogActions sx={{px: 2.5, pb: 2.5}}>
                <Button variant="outlined" disabled={request.isPending} onClick={onClose}>Cancel</Button>
                <Button
                    variant="contained"
                    loading={request.isPending}
                    onClick={submit}
                >
                    Send request
                </Button>
            </DialogActions>
        </Dialog>
    );
};
